import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import EntryCard from "@/components/feature/EntryCard";
import useSeo from "@/hooks/useSeo";
import { getEntryById, getCategoryBySlug, getRelatedEntries, formatDate, formatHeat, getCategoryName } from "@/utils/content";
import { entries } from "@/mocks/entries";
import { hotKeywords } from "@/mocks/keywords";

export default function EntryPage() {
  const { id = "" } = useParams();
  const entry = getEntryById(id);
  const origin = window.location.origin;

  useSeo({
    title: entry ? `${entry.title} - 15黑料网` : "词条不存在 - 15黑料网",
    description: entry
      ? `${entry.summary}本词条收录时间 ${formatDate(entry.date)}，最近更新 ${formatDate(entry.updated)}。`
      : "该词条不存在或已调整，请返回索引页重新检索。",
    keywords: entry
      ? `${entry.persona},${entry.tags.join(",")},黑料汇总,15黑料网`
      : "15黑料网,黑料汇总",
    path: `/entry/${id}`,
    jsonLd: entry
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: entry.title,
            description: entry.summary,
            inLanguage: "zh-CN",
            datePublished: entry.date,
            dateModified: entry.updated,
            articleSection: getCategoryName(entry.category),
            keywords: entry.tags.join(","),
            author: { "@type": "Organization", name: "15黑料网" },
            publisher: { "@type": "Organization", name: "15黑料网" },
            mainEntityOfPage: `${origin}/entry/${entry.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: origin },
              { "@type": "ListItem", position: 2, name: "热门索引", item: `${origin}/keywords` },
              {
                "@type": "ListItem",
                position: 3,
                name: getCategoryName(entry.category),
                item: `${origin}/category/${entry.category}`,
              },
              { "@type": "ListItem", position: 4, name: entry.title, item: `${origin}/entry/${entry.id}` },
            ],
          },
        ]
      : undefined,
  });

  if (!entry) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-[1280px] flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-file-unknow-line text-2xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这个汇总</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            词条可能已被合并或重命名。回到索引页重新搜索关键词，通常都能找到对应的新入口。
          </p>
          <Link
            to="/keywords"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-line" />
            去索引页搜索
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const category = getCategoryBySlug(entry.category);
  const related = getRelatedEntries(entry.related);
  const sideKeywords = [...hotKeywords].sort((a, b) => b.heat - a.heat).slice(0, 8);
  const moreEntries = entries.filter((item) => item.id !== entry.id && item.category !== entry.category).slice(0, 3);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
          <Breadcrumbs
            items={[
              { label: "首页", path: "/" },
              { label: "热门索引", path: "/keywords" },
              { label: getCategoryName(entry.category), path: `/category/${entry.category}` },
              { label: entry.title },
            ]}
          />
        </div>

        <div className="mx-auto grid max-w-[1280px] gap-10 px-4 pb-14 md:px-6 lg:grid-cols-[1fr_320px] lg:gap-12">
          <article>
            <header>
              <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                <Link
                  to={`/category/${entry.category}`}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {getCategoryName(entry.category)}
                </Link>
                <span className="flex items-center gap-1 text-accent-700">
                  <i className="ri-fire-line" />
                  热度 {formatHeat(entry.heat)}
                </span>
                <span>{formatDate(entry.date)} 首次整理</span>
                <span>·</span>
                <span>{formatDate(entry.updated)} 最近更新</span>
              </div>
              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                {entry.title}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{entry.summary}</p>
            </header>

            <figure className="mt-6 overflow-hidden rounded-lg bg-background-100">
              <img
                src={entry.cover}
                alt={`${entry.title}话题汇总配图`}
                title={`${entry.persona} 相关话题汇总配图`}
                className="h-56 w-full object-cover object-top md:h-80"
              />
            </figure>

            <section className="mt-8" aria-labelledby="entry-body-title">
              <h2 id="entry-body-title" className="font-heading text-xl font-bold text-foreground-950">
                事件梳理
              </h2>
              <div className="mt-4 space-y-4">
                {entry.body.map((paragraph, index) => (
                  <p key={index} className="text-sm leading-[1.9] text-foreground-700 md:text-base">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            <section className="mt-10" aria-labelledby="entry-timeline-title">
              <h2 id="entry-timeline-title" className="font-heading text-xl font-bold text-foreground-950">
                时间线
              </h2>
              <ol className="mt-5 border-l-2 border-background-200 pl-0">
                {entry.timeline.map((item) => (
                  <li key={`${item.date}-${item.text}`} className="relative pb-6 pl-6 last:pb-0">
                    <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full border-2 border-background-50 bg-accent-500" />
                    <p className="text-xs font-medium tabular-nums text-primary-600">{item.date}</p>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                  </li>
                ))}
              </ol>
            </section>

            <section className="mt-10" aria-labelledby="entry-tag-title">
              <h2 id="entry-tag-title" className="font-heading text-lg font-bold text-foreground-950">
                相关标签
              </h2>
              <div className="mt-4 flex flex-wrap gap-2.5">
                {entry.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/keywords?q=${encodeURIComponent(tag)}`}
                    className="whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-4 py-1.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </section>

            <section className="mt-10" aria-labelledby="entry-related-title">
              <h2 id="entry-related-title" className="font-heading text-xl font-bold text-foreground-950">
                相关汇总推荐
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((item) => (
                  <EntryCard key={item.id} entry={item} />
                ))}
              </div>
            </section>
          </article>

          <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
            {category && (
              <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-5">
                <h2 className="text-sm font-semibold text-foreground-950">所属分类</h2>
                <Link
                  to={`/category/${category.slug}`}
                  className="mt-4 flex items-center gap-3 rounded-md bg-background-50 p-3 transition-colors hover:bg-background-100"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className={`${category.icon} text-lg`} />
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-950">{category.name}</span>
                    <span className="block text-xs text-foreground-500">{category.count} 条汇总</span>
                  </span>
                </Link>
              </div>
            )}

            <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                <i className="ri-fire-line text-accent-500" />
                热门黑料词
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {sideKeywords.map((keyword) => (
                  <Link
                    key={keyword.word}
                    to={`/entry/${keyword.entryId}`}
                    className="whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    {keyword.word}
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-5">
              <h2 className="text-sm font-semibold text-foreground-950">换个话题</h2>
              <ul className="mt-3">
                {moreEntries.map((item) => (
                  <li key={item.id} className="border-b border-background-200/70 py-3 last:border-b-0">
                    <Link
                      to={`/entry/${item.id}`}
                      className="group block text-sm leading-relaxed text-foreground-800 transition-colors hover:text-primary-600"
                    >
                      <span className="line-clamp-2">{item.title}</span>
                      <span className="mt-1 block text-xs text-foreground-500">
                        {getCategoryName(item.category)} · 热度 {formatHeat(item.heat)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-foreground-950 p-5">
              <h2 className="text-sm font-semibold text-background-50">找到的信息不完整？</h2>
              <p className="mt-2 text-xs leading-relaxed text-background-300">
                如果你知道该事件的后续公开回应，欢迎通过邮箱补充线索，核实后我们会更新词条。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-mail-send-line" />
                提交线索
              </Link>
            </div>
          </aside>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}